import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {select, Store} from '@ngrx/store';

import {DeviceType, Group, IGroupedDevice} from '../../shared';
import {environment} from '../../../environments/environment';
import * as fromRoot from '../../reducers';
import * as fromAfbApi from '../../afb-api/reducers';

import {combineLatest, Observable} from 'rxjs';

import {Device} from '../../afb-api/models/device';
import {Simple200Response} from '../../afb-api/models/general';
import {map} from 'rxjs/internal/operators';

@Injectable()
export class GroupService {

  constructor(private httpClient: HttpClient,
              private afbApiStore: Store<fromAfbApi.State>,
              private store: Store<fromRoot.State>) {
  }

  /**
   * Group APIs
   */
  createGroup(orgToken: string, name: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${orgToken}`,
      'Content-Type': 'application/json'
    });
    const url = `${environment.afbV2Host}orgs/self/groups`;
    const body = {name};

    return this.httpClient
      .post<Simple200Response>(url, body, {headers});
  }

  requestMyGroupList(orgToken: string): Observable<Group[]> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}org-users/self/groups`;

    return this.httpClient
      .get<{ data: Group[] }>(url, {headers})
      .pipe(
        map(response => response.data)
      );
  }

  assignGroup(orgToken: string, groupId: string, deviceType: string, deviceId: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${orgToken}`,
      'Content-Type': 'application/json'
    });

    const url = `${environment.afbV2Host}org-users/self/groups/${groupId}/devices/${deviceType}/${deviceId}`;

    return this.httpClient
      .post<Simple200Response>(url, null, {headers});
  }

  removeDeviceFromGroup(orgToken: string, groupId: string, deviceType: string, deviceId: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}org-users/self/groups/${groupId}/devices/${deviceType}/${deviceId}`;

    return this.httpClient
      .delete<Simple200Response>(url, {headers});
  }

  deleteGroup(orgToken: string, groupId: string): Observable<Simple200Response> {
    const headers = new HttpHeaders({'Authorization': `Bearer ${orgToken}`});
    const url = `${environment.afbV2Host}org-users/self/groups/${groupId}`;

    return this.httpClient
      .delete<Simple200Response>(url, {headers});
  }

  getSelectedGroupedDevice(deviceList: Device[], selectedGroup: Group): IGroupedDevice {
    if (deviceList == null) {
      return null;
    } else {
      const filterDeviceList = deviceList.filter((device: Device) => selectedGroup == null ? true : device.groupId === selectedGroup.id);
      return {
        id: selectedGroup != null ? selectedGroup.id : null,
        name: selectedGroup != null ? selectedGroup.name : null,
        deviceList: filterDeviceList,
        hasAwair: filterDeviceList.filter((device: Device) => device.deviceType === DeviceType.Awair).length > 0,
        hasGlow: filterDeviceList.filter((device: Device) => device.deviceType === DeviceType.Glow).length > 0,
        hasMint: filterDeviceList.filter((device: Device) => device.deviceType === DeviceType.Mint).length > 0,
        hasOmni: filterDeviceList.filter((device: Device) => device.deviceType === DeviceType.Omni).length > 0
      }
    }
  }

  getGroupedDeviceList(): Observable<IGroupedDevice[]> {
    const deviceList$: Observable<Device[]> = this.afbApiStore.pipe(
      select(fromAfbApi.devices)
    );
    const groupList$: Observable<Group[]> = this.store.pipe(
      select(fromRoot.getCoreGroupList)
    );

    return combineLatest(deviceList$, groupList$, (deviceList: Device[], groupList: Group[]) => {
      return this.makeGroupedDeviceList(deviceList, groupList);
    });
  }

  private makeGroupedDeviceList(deviceList: Device[], groupList: Group[]): IGroupedDevice[] {
    const groupedDeviceList: IGroupedDevice[] = [];

    // Create copy to not mutate original objects
    const deviceListSorted = [...deviceList];
    if (deviceListSorted.length === 1 && deviceListSorted[0].groupId == null) {
      // Sort function won't execute if there's only one element in the array
      deviceListSorted[0].groupId = null;
    } else {
      deviceListSorted.sort(this.sortDeviceListByGroupId);
    }

    const groupListSorted = [...groupList];
    groupListSorted.sort(this.sortGroupListById);

    if (groupListSorted.length === 0) {
      // All devices are non-grouped if there's no group in the list
      groupedDeviceList.push({
        id: null,
        name: null,
        deviceList: deviceListSorted,
        hasAwair: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Awair).length > 0,
        hasGlow: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Glow).length > 0,
        hasMint: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Mint).length > 0,
        hasOmni: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Omni).length > 0
      });
    } else {
      // If at least one group is present
      for (let i = 0; i < groupListSorted.length; i++) {
        groupedDeviceList.push({
          id: groupListSorted[i].id,
          name: groupListSorted[i].name,
          deviceList: deviceListSorted.filter((device: Device) => device.groupId === groupListSorted[i].id),
          hasAwair: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Awair).length > 0,
          hasGlow: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Glow).length > 0,
          hasMint: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Mint).length > 0,
          hasOmni: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Omni).length > 0
        });
      }
      // Non-grouped, Unassigned
      groupedDeviceList.push({
        id: null,
        name: null,
        deviceList: deviceListSorted.filter((device: Device) => device.groupId == null),
        hasAwair: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Awair).length > 0,
        hasGlow: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Glow).length > 0,
        hasMint: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Mint).length > 0,
        hasOmni: deviceListSorted.filter((eachDevice: Device) => eachDevice.deviceType === DeviceType.Omni).length > 0
      });
    }

    return groupedDeviceList;
  }

  private sortDeviceListByGroupId(deviceA: Device, deviceB: Device): number {
    // Set groupId to -1 if non-grouped
    // Modifying the real object to assign groupId to non-grouped devices
    if (deviceA.groupId == null) {
      deviceA.groupId = null;
    }
    if (deviceB.groupId == null) {
      deviceB.groupId = null;
    }

    return ('' + deviceA.groupId).localeCompare(deviceB.groupId);
    // return deviceA.groupId - deviceB.groupId;
  }

  private sortGroupListById(groupA: Group, groupB: Group): number {
    return ('' + groupA.id).localeCompare(groupB.id);
    // return groupA.id - groupB.id;
  }
}
